import { useState, useEffect } from 'react';
import type { CardFinish } from '../components/CollectionButton';
import { fetchCardFinishes, mapScrydexFinishToCollection } from '../utils/cardPricing';

/**
 * Hook that returns the available card finishes for a specific card
 * based on Scrydex pricing data. Returns null while loading or if
 * no data is available (caller should fall back to showing all finishes).
 *
 * Listens for 'scrydexFinishesLoaded' events so it auto-updates when
 * the ScrydexPricing component fetches fresh data from the API.
 */
export function useCardFinishes(cardId: string | undefined): CardFinish[] | null {
  const [finishes, setFinishes] = useState<CardFinish[] | null>(null);

  useEffect(() => {
    if (!cardId) {
      setFinishes(null);
      return;
    }

    let cancelled = false;
    fetchCardFinishes(cardId).then(result => {
      if (!cancelled) setFinishes(result);
    });

    const handleScrydexUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.cardId === cardId && detail?.finishes?.length > 0) {
        const mapped = [...new Set(detail.finishes.map(mapScrydexFinishToCollection))] as CardFinish[];
        if (!cancelled) setFinishes(mapped);
      }
    };

    document.addEventListener('scrydexFinishesLoaded', handleScrydexUpdate);

    return () => {
      cancelled = true;
      document.removeEventListener('scrydexFinishesLoaded', handleScrydexUpdate);
    };
  }, [cardId]);

  return finishes;
}
